import React, { useEffect, useState, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from 'primereact/inputtext';

const StepTwo = ({ handleChange, quizObject, isValid }) => {
    const [pages, setPages] = useState(quizObject || []);
    const [skipPrequal, setSkipPrequal] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [pageToDelete, setPageToDelete] = useState(null);

    const toast = useRef(null);

    const questionTypeOptions = [
        { label: 'Réponse courte', value: 'short' },
        { label: 'Réponse longue', value: 'long' },
        { label: 'Choix multiples', value: 'mcq-m' },
        { label: 'Choix unique', value: 'mcq-u' },
        { label: 'Choix unique (Likert)', value: 'likert' },
    ];

    useEffect(() => {
        if (skipPrequal) return;

        let is_valid = true;

        if (!pages?.length) is_valid = false;

        pages.forEach(page => {
            if (!page.questions.length) is_valid = false;
            if (!page.title.length) is_valid = false;

            page.questions.forEach(question => {
                if (!question.question) is_valid = false;
                if (question.type === 'Choisissez le type de question') is_valid = false;

                if (['mcq-m', 'mcq-u'].includes(question.type)) {
                    if (question?.choices?.length === 0) is_valid = false;
                    else if (question?.choices[0]?.length === 0) is_valid = false;
                };
            });
        });

        // Sending back the data to be treated in index.jsx 👍
        handleChange(pages);
        isValid(is_valid ? { step: '1' } : false);
    }, [pages, handleChange, isValid]);

    useEffect(() => {
        if (skipPrequal) {
            handleChange([]);
            return isValid({ step: '1' });
        };

        return isValid(false);
    }, [skipPrequal]);

    // TODO: Rajouter un moyen d'avoir un titre de page.
    // TODO: Rajouter un moyen de changer un test is_active ou pas.
    const handleAddPage = () => {
        if (pages.length >= 30) return toast.current.show({
            severity: "warning",
            summary: "Vous venez d'atteindre la limite de pages totales possibles",
            life: 10000
        });  // Preventing more than Max 30 pages

        const newPage = {
            id: `page-${new Date().getTime()}`,
            is_active: true,
            title: ``,
            questions: []
        };
        setPages([...pages, newPage]);
    };

    const handleDeletePage = (pageId) => {
        const updatedPages = pages.filter(page => page.id !== pageId);
        setPages(updatedPages);
        setPageToDelete(null);
        toast.current.clear();
    };

    const confirmDeletePage = (pageId) => {
        if (pageToDelete) {
            return toast.current.show({
                severity: "danger",
                summary: "Veuillez d'abord confirmer la suppression de la première page.",
                sticky: true,
            });
        };

        const page = pages.find(page => page.id === pageId);
        if (page.questions.length === 0) {
            handleDeletePage(pageId);
        } else {
            setPageToDelete(pageId);
            toast.current.show({
                severity: 'warn',
                summary: 'Confirmation',
                detail: (
                    <div>
                        <p>Voulez-vous vraiment supprimer cette page ?</p>
                        <div className="buttons">
                            <button onClick={() => handleDeletePage(pageId)} className="button is-warning" >Oui, supprimer</button>
                            <button onClick={() => handleDeletePage(null)} className="button is-secondary">Non, annuler</button>
                        </div>
                    </div>
                ),
                sticky: true,
            });
        }
    };

    const handleDeleteAllPages = () => {
        setPages([]);
    };

    const handleAddQuestion = (pageId) => {
        const newQuestion = {
            id: `question-${new Date().getTime()}`,
            question: '',
            type: 'Choisissez le type de question',
            choices: [''],
            subQuestions: [],  // For likert 
        };
        const updatedPages = pages.map(page => {
            if (page.id === pageId) {
                if (page.questions.length >= 20) return toast.current.show({
                    severity: "warning",
                    summary: "Vous venez d'atteindre la limite de questions sur cette page",
                    life: 10000
                });  // Preventing more than Max 20 questions

                return { ...page, questions: [...page.questions, newQuestion] };
            }
            return page;
        });
        setPages(updatedPages);
        setExpanded({ ...expanded, [newQuestion.id]: true });
    };

    const handleDeleteQuestion = (pageId, questionId) => {
        const updatedPages = pages.map(page => {
            if (page.id === pageId) {
                const updatedQuestions = page.questions.filter(question => question.id !== questionId);
                return { ...page, questions: updatedQuestions };
            }
            return page;
        });
        setPages(updatedPages);
    };

    const handleDeleteAllQuestions = (pageId) => {
        const updatedPages = pages.map(page => {
            if (page.id === pageId) {
                return { ...page, questions: [] };
            }
            return page;
        });
        setPages(updatedPages);
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;

        const sourcePage = pages.find(page => page.id === source.droppableId);
        const destPage = pages.find(page => page.id === destination.droppableId);
        const [reorderedQuestion] = sourcePage.questions.splice(source.index, 1);

        if (source.droppableId === destination.droppableId) {
            sourcePage.questions.splice(destination.index, 0, reorderedQuestion);
        } else {
            destPage.questions.splice(destination.index, 0, reorderedQuestion);
        }

        setPages(pages.map(page => {
            if (page.id === sourcePage.id) return sourcePage;
            if (page.id === destPage.id) return destPage;
            return page;
        }));
    };

    const toggleQuestion = (id) => {
        setExpanded({
            ...expanded,
            [id]: !expanded[id]
        });
    };

    const updatePageTitle = (pageId, title) => {
        const updatedPages = pages.map(page => {
            if (page.id === pageId) {
                return { ...page, title };
            }
            return page;
        });

        setPages(updatedPages);
    };

    const updateQuestion = (pageId, questionId, key, value) => {
        const updatedPages = pages.map(page => {
            if (page.id === pageId) {
                const updatedQuestions = page.questions.map(question => {
                    if (question.id === questionId) {
                        return { ...question, [key]: value };
                    }
                    return question;
                });
                return { ...page, questions: updatedQuestions };
            }
            return page;
        });
        setPages(updatedPages);
    };

    const handleChoiceChange = (pageId, questionId, choiceIndex, value) => {
        const updatedPages = pages.map(page => {
            if (page.id === pageId) {
                const updatedQuestions = page.questions.map(question => {

                    if (question.id === questionId) {
                        const updatedChoices = question.choices.map((choice, index) => {
                            if (index === choiceIndex) return value;
                            return choice;
                        });

                        if (choiceIndex === question.choices.length - 1 && value) updatedChoices.push('');

                        return { ...question, choices: updatedChoices };
                    }
                    return question;
                });
                return { ...page, questions: updatedQuestions };
            };

            return page;
        });
        setPages(updatedPages);
    };

    const showToast = () => {
        toast.current.show({ severity: 'info', summary: 'Rappel', detail: 'On vous recommande fortement les Questions à Choix Multiples, cela vous permettra de facilement filtrer les réponses de vos participants.', life: 30000 });
    };

    useEffect(() => {
        if (sessionStorage.getItem('toastStep2')) return;

        showToast();

        sessionStorage.setItem('toastStep2', true);
    }, []);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div id='campaignStep2'>
                <Toast ref={toast} position="bottom-right" />

                <div className='columns is-vcentered'>

                    <div className="column is-full">
                        <Checkbox inputId="skipPrequalCheckbox" onChange={(e) => setSkipPrequal(e.target.checked)} checked={skipPrequal} />
                        <label htmlFor="skipPrequalCheckbox" className='is-clickable'> Je ne souhaite pas ajouter de questions de pré-qualification</label>
                    </div>
                </div>

                <hr />

                <button className="button is-info mt-2 mb-5 mr-3" onClick={handleAddPage} disabled={skipPrequal}>
                    <span className="icon">
                        <i className="fa-solid fa-plus"></i>
                    </span>
                    <span className='p-2'>Nouvelle page {pages?.length > 0 && `- ${pages?.length}`}</span>
                </button>

                {/* <button className="button is-danger mt-2 mb-5" onClick={handleDeleteAllPages}>
                    <span className="icon">
                        <i className="fas fa-trash-alt"></i>
                    </span>
                    <span>Supprimer toutes les pages</span>
                </button> */}

                {pages.map((page, pageIndex) => (
                    <Droppable key={page.id} droppableId={page.id}>
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="box radius-20">
                                <div>
                                    <div className="media-content">
                                        <p onClick={() => toggleQuestion(page.id)} className='is-clickable'>
                                            <strong>
                                                {pageIndex + 1} {`- ${page.title}`}
                                                <span className="icon is-small is-pulled-right">
                                                    <i className={`fas fa-chevron-${expanded[page.id] ? 'down' : 'right'}`}></i>
                                                </span>
                                            </strong>
                                        </p>

                                        {expanded[page.id] && (
                                            <div>

                                                <div className="">
                                                    <InputText
                                                        value={page.title}
                                                        onChange={(e) => updatePageTitle(page.id, e.target.value)}
                                                        placeholder="Titre de la page"
                                                    />
                                                </div>

                                                <button className="button is-info mb-5 mt-2 mr-2" onClick={() => handleAddQuestion(page.id)}>
                                                    <span className="icon">
                                                        <i className="fa-solid fa-plus"></i>
                                                    </span>
                                                    <span className='p-1'>Nouvelle question {page?.questions?.length > 0 ? `- ${page.questions.length}` : ''}</span>
                                                </button>

                                                <button className="button is-danger is-light mb-5 mt-2" onClick={() => confirmDeletePage(page.id)}>
                                                    <span className="icon">
                                                        <i className="fas fa-trash-alt"></i>
                                                    </span>
                                                    <span>Supprimer cette page</span>
                                                </button>

                                                {/* <button className="button is-danger mb-5 mt-2" onClick={() => handleDeleteAllQuestions(page.id)}>
                                                    <span className="icon">
                                                        <i className="fas fa-trash-alt"></i>
                                                    </span>
                                                    <span>Tous supprimer</span>
                                                </button> */}

                                                {page.questions.map((question, questionIndex) => (
                                                    <Draggable key={question.id} draggableId={question.id} index={questionIndex}>
                                                        {(provided) => (
                                                            <div ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className="box">

                                                                <article className="">
                                                                    <div className="media-content">
                                                                        <div className="">

                                                                            <p onClick={() => toggleQuestion(question.id)} className='is-clickable'>
                                                                                <strong>
                                                                                    {questionIndex + 1} - {questionTypeOptions.find(option => option.value === question.type)?.label || ''} {question.question ? '| ' + question.question : ''}
                                                                                    <span className="icon is-small is-pulled-right">
                                                                                        <i className={`fas fa-chevron-${expanded[question.id] ? 'down' : 'right'}`}></i>
                                                                                    </span>
                                                                                </strong>
                                                                            </p>

                                                                            {expanded[question.id] && (
                                                                                <div>
                                                                                    <div className="field">
                                                                                        <label className="label mt-2">- Question</label>
                                                                                        <div className="control">
                                                                                            <input
                                                                                                className="input"
                                                                                                type="text"
                                                                                                value={question.question}
                                                                                                onChange={(e) => updateQuestion(page.id, question.id, 'question', e.target.value)}
                                                                                                disabled={skipPrequal}
                                                                                            />
                                                                                        </div>
                                                                                    </div>

                                                                                    <Dropdown
                                                                                        value={question.type}
                                                                                        options={questionTypeOptions}
                                                                                        onChange={(e) => updateQuestion(page.id, question.id, 'type', e.value)}
                                                                                        placeholder="Type de question"
                                                                                        disabled={skipPrequal}
                                                                                    />


                                                                                    <hr />

                                                                                    <label className="label mt-2">- Réponse</label>

                                                                                    <div className="mt-2">
                                                                                        {['mcq-m', 'mcq-u'].includes(question.type) && question.choices.map((choice, choiceIndex) => (
                                                                                            <input key={choiceIndex}
                                                                                                className="mt-1 input"
                                                                                                type="text"
                                                                                                placeholder={`Choix ${choiceIndex + 1}`}
                                                                                                value={choice}
                                                                                                onChange={(e) => handleChoiceChange(page.id, question.id, choiceIndex, e.target.value)}
                                                                                                disabled={skipPrequal}
                                                                                            />
                                                                                        ))}

                                                                                        {question.type === 'Choisissez le type de question' && (
                                                                                            <input key={`defaultInput-${new Date().getTime()}`}
                                                                                                className="mt-1 input"
                                                                                                type="text"
                                                                                                value="Veuillez choisir le type de question..."
                                                                                                readOnly
                                                                                                disabled
                                                                                            />
                                                                                        )}

                                                                                        {question.type === 'short' && (
                                                                                            <input
                                                                                                className="input is-not-allowed mt-2"
                                                                                                type="text"
                                                                                                value="L'utilisateur répondra par une courte réponse de maximum 280 caractères."
                                                                                                readOnly
                                                                                                disabled
                                                                                            />
                                                                                        )}

                                                                                        {question.type === 'long' && (
                                                                                            <textarea
                                                                                                className="textarea is-not-allowed mt-2"
                                                                                                value="L'utilisateur répondra par une réponse libre de maximum 2048 caractères."
                                                                                                readOnly
                                                                                                disabled
                                                                                            ></textarea>
                                                                                        )}

                                                                                        {question.type === 'likert' && (
                                                                                            <div className="likert-scale-container">
                                                                                                <table className="table is-striped is-hoverable is-fullwidth">
                                                                                                    <thead className='has-text-centered'>
                                                                                                        <tr>
                                                                                                            <th>Question</th>
                                                                                                            <th>Pas du tout d'accord</th>
                                                                                                            <th>Pas d'accord</th>
                                                                                                            <th>Sans opinion</th>
                                                                                                            <th>Plutôt d'accord</th>
                                                                                                            <th>Tout à fait d'accord</th>
                                                                                                        </tr>
                                                                                                    </thead>
                                                                                                    <tbody>
                                                                                                        {question.choices.map((choice, choiceIndex) => (
                                                                                                            <tr key={choiceIndex}>
                                                                                                                <td>
                                                                                                                    <InputTextarea
                                                                                                                        autoResize
                                                                                                                        className='p-2'
                                                                                                                        placeholder={` Question ${choiceIndex + 1}`}
                                                                                                                        value={choice}
                                                                                                                        onChange={(e) => handleChoiceChange(page.id, question.id, choiceIndex, e.target.value)}
                                                                                                                        rows={3}
                                                                                                                        cols={30}
                                                                                                                        disabled={skipPrequal} />
                                                                                                                </td>
                                                                                                                {[1, 2, 3, 4, 5].map((num) => (
                                                                                                                    <td key={num} style={{ textAlign: 'center' }}>
                                                                                                                        <input
                                                                                                                            type="radio"
                                                                                                                            name={`likert-${question.id}-${choiceIndex}`}
                                                                                                                            value={num}
                                                                                                                            disabled
                                                                                                                        />
                                                                                                                    </td>
                                                                                                                ))}
                                                                                                            </tr>
                                                                                                        ))}
                                                                                                    </tbody>
                                                                                                </table>
                                                                                            </div>
                                                                                        )}

                                                                                    </div>

                                                                                    <button className="button is-danger is-light is-small mb-5 mt-2" onClick={() => handleDeleteQuestion(page.id, question.id)}>
                                                                                        <span className="icon">
                                                                                            <i className="fas fa-trash-alt"></i>
                                                                                        </span>
                                                                                        <span className='has-text-black'>Supprimer cette question</span>
                                                                                    </button>

                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </div>

                                                                </article>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}

                                                {provided.placeholder}

                                                {page?.questions?.length >= 3 && <button className="button is-info mb-5 mt-2 mr-2" onClick={() => handleAddQuestion(page.id)}>
                                                    <span className="icon">
                                                        <i className="fa-solid fa-plus"></i>
                                                    </span>
                                                    <span className='p-1'>Nouvelle question {page?.questions?.length > 0 ? `- ${page.questions.length}` : ''}</span>
                                                </button>}

                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Droppable>
                ))}

                {pages.length > 3 &&
                    <button className="button is-info mt-2 mb-5" onClick={handleAddPage} disabled={skipPrequal}>
                        <span className="icon">
                            <i className="fa-solid fa-plus"></i>
                        </span>
                        <span className='p-2'>Nouvelle page {pages?.length > 0 && `- ${pages?.length}`}</span>
                    </button>
                }

            </div>

            {/* Don't remove */}
            <hr className="is-invisible mt-6" />
        </DragDropContext >
    );
};

export default StepTwo;